import React, {useEffect, useRef, useMemo} from 'react'
import _ from 'lodash/fp'
import ClipboardJS from 'clipboard'
import {Dialog, ContextMenu, Li, Button, Icon, willUseFlipSwitch, Loading} from '@startlibs/components'
import {
  ToggleCheckbox,
  WithForm,
  AutoComplete,
  useFormValue,
  Errors,
  FormValue,
  Field,
  TextInput
} from '@startlibs/form'
import {useLazyConstant, usePopupToggle, useToggle} from '@startlibs/core'
import {LoadingErrorDialog} from './LoadingErrorDialog'
import styled, {css} from 'styled-components'
import {callIfFunction, getColor} from '@startlibs/utils'
import {twoWayAnimation, media, formFetch, postFetcher} from '@startlibs/utils'
import {useNotificationRef} from '../components/Notifications'
import {getStudyDescription} from '../utils'

const [onEnter, onLeave] = twoWayAnimation('opacity:0; transform:translateY(50%)', 'opacity:1;transform:translateY(0)')
const [loadingEnter, loadingLeave] = twoWayAnimation('opacity:0;', 'opacity:1;')


const SharingDialog = styled(Dialog)`
  .suggested-emails {
    margin-top: -0.5rem;
    margin-bottom: 1.25rem;
    font-weight: 600;
    a {
      text-decoration: underline;
      font-weight: 400;
      color: ${getColor('main')};
    }
  }
  ${media.max(430)`
    ${Button} {
      min-width: unset;
      padding-left: 1rem;
      padding-right: 1rem;
      & ~ ${Button} {
        margin-left: 0.75rem;
      }
    }
  `}
  ${media.max(390)`
    padding: 1rem;
    ${Button} {
      padding-left: 0.75rem;
      padding-right: 0.75rem;
      ${Icon} {
        display: none;
      }
    }
  `}
`

const AdditionalText = styled.p`
  margin-top: .5rem;
`

const EXPIRATION_TIMES = [0, 48, 168]

const EXPIRATION_LABELS = {
  [0]: 'Never (access link will not expire)',
  [48]: '48 hours (recipients will not have access after 48 hours)',
  [168]: '1 week (recipients will not have access after 1 week)'
}

export const ExamIdentification = styled.div`
  border-radius: 6px;
  border: 1px solid rgba(0,0,0,0.25);
  padding: 0.75rem 1rem;
  margin-bottom: 1rem;
  .study-info {
    color: rgba(0,0,0,0.7);
    margin-top: 2px;
    :first-child {
      font-size: 14px;
      font-weight: 600;
      color: inherit;
      margin-top: 0;
    }
  }
  ${ToggleCheckbox} {
    margin: 0.75rem -1rem -0.75rem;
    border-top-left-radius: 0;
    border-top-right-radius: 0;
    border-bottom-left-radius: 5px;
    border-bottom-right-radius: 5px;
  }
`
const ToggleCheckboxComponent = styled(ToggleCheckbox)`
  font-weight: 600;
  ${props => props.disabled && `
    opacity: 0.8;
    pointer-events:none;
  `}
`
const EmailConfirmationListContainer = styled.div`
  background-color: rgba(0,0,0,.07);
  border: 1px solid rgba(0,0,0,.1);
  border-radius: 5px;
  margin-bottom: 1rem;
  font-size: 13px;
  padding: 1rem;
  p {
    margin-bottom: 0;
  }
  ul {
    list-style-type: disc;
    margin-top: .5rem;
    margin-left: 2rem;
  }
`
const SecurityDataWarning = styled.div`
  color: rgba(0,0,0,0.5);
  h4 {
    font-size: 13px;
    margin-bottom: 0.25rem;
  }
  p {
    font-size: 12px;
    margin-bottom: 0;
  }
`
const FieldDescription = styled.div`
  color: rgba(0,0,0,0.5);
  font-size: 12px;
  margin-top: -0.5rem;
`

const validateEmail = (email) => {
  const re = /^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/i
  return re.test(email)
}
const emailValidation = (v = '') => validateEmail(v.trim().replace(/\u200b/g, '')) ? {success: v.trim().replace(/\u200b/g, '')} : {errors: [`"${v}" is not a valid e-mail address.`]}

const preValidation = ({emails}) => {
  const hasEmails = emails.find(e => !!e)
  const invalidEmails = emails.filter(e => e && !validateEmail(e))
  if (!hasEmails)
    return {emails: [`At least one valid e-mail address is needed.`]}
  if (invalidEmails.length) {
    return {}
  }
}
// virtualbox test
// const SPA_ORIGIN = location.hostname === "10.0.2.2" ? "" :'https://share.purview.net'
export const SPA_ORIGIN = () => window.SHARE_ORIGIN || 'https://share.purview.net'

export const AUTHORIZE = (jwt, origin = SPA_ORIGIN()) => postFetcher(origin + '/api/system/authenticate?t=' + jwt)
export const GET_LINK = (jwt, params, origin = SPA_ORIGIN()) => postFetcher(origin + '/api/share/link?t=' + jwt, params)

export const ExamCard = ({study, patientName}) => <ExamIdentification>
  {patientName && <div className="study-info">{patientName}</div>}
  <div className="study-info">{getStudyDescription(study)}</div>
  <div className="study-info">Referring Physician: {study.referringPhysician}</div>
</ExamIdentification>

const defaultInfoCard = ({patientName,record, study = record}) => <>
  {patientName && <div className="study-info">{patientName}</div>}
  <div className="study-info">{getStudyDescription(study)}</div>
  <div className="study-info">Referring Physician: {study.referringPhysician}</div>
</>

const DEFAULT_FORM_VALUES = ({providerInfo,shareDefaults,hasReport, caseRequest}) => {
  const shareStudyReport = providerInfo.sharingOptions.includeStudyReport === 2 && hasReport
  let values = {emails: [], ...providerInfo.sharingOptions, shareStudyReport}
  if (!providerInfo.phiProtectionImplemented) {
    values = {...values,sendSecurely:false}
  }
  if (caseRequest) {
    values = {...values,...shareDefaults}
  }
  return values
}

export const ShareDialog = (
  {
    closeDialog, caseRequest, caseRequestInfo, notifyAndClose, jwt, suggestedEmails = [], labels = {},
    shareDefaults = {allowSubmit: false, shareCase: true},
    shareableLinkIsOpen, authorize = AUTHORIZE, infoCard = caseRequestInfo || defaultInfoCard, defaultFormValues = DEFAULT_FORM_VALUES, transform: rawTransform, getLink=GET_LINK, authorizationIsOpen,
    shareFetch,
    ...rest
  }) => {
  const error = useToggle()
  const authorization = useToggle(authorizationIsOpen)
  const formRef = useRef()
  useEffect(() => {
    if (jwt) {
      authorize(jwt)
        .then((auth) => {
          authorization.openWith(auth)
        })
        .catch(error.open)
    }
  }, [jwt])

  const [, setNotification] = useNotificationRef()
  const closeWithNotification = (notification, notificationType) => {
    if (notifyAndClose) {
      notifyAndClose(notification, notificationType)
    } else {
      setNotification(notification)
      closeDialog()
    }
  }


  const transform = rawTransform || (({emails, ...sharingOptions}) => caseRequest

    ? ({
      mailsTo: _.filter(email => email, emails),
      sharingOptions,
      studyUID: caseRequest.requestCode
    })
    : ({
      mailsTo: _.filter(email => email, emails),
      sharingOptions,
      studyUID: authorization.get().study.studyUID
    }))

  /* Hook rules, can't have hooks after if, this return is just a regular function call, so rules apply */
  const confirmDialog = useToggle()
  const shareableLink = useToggle(shareableLinkIsOpen)
  const shareLink = useToggle()

  const formAction = confirmDialog.isOpen ? (shareFetch || formFetch(SPA_ORIGIN() + '/api/share?t=' + authorization.isOpen.jwt)) : confirmDialog.open

  const flipAnimation = useLazyConstant(willUseFlipSwitch)

  if (error.isOpen) {
    return <LoadingErrorDialog errorCode="02" closeDialog={closeDialog}/>
  }

  return <WithForm
    alwaysSave
    skipDom
    ref={formRef}
    action={formAction}
    transform={transform}
    onSuccess={() => {
      if (confirmDialog.isOpen) {
        closeWithNotification('Records successfully shared via email', 'email')
      }
    }}
    preValidation={preValidation}
  >{form =>
    authorization.isOpen
      ? AuthorizedShareDialog({
        closeDialog,
        closeWithNotification,
        authorization: authorization.isOpen,
        confirmDialog,
        shareableLink,
        shareLink,
        transform,
        flipAnimation,
        defaultFormValues,
        suggestedEmails,
        caseRequest,
        infoCard,
        form,
        getLink,
        shareDefaults,
        labels,
        ...rest
      })
      : LoadingDialog({closeDialog, form, flipAnimation, caseRequest, labels, ...rest})
  }</WithForm>
}

const LoadingDialog = ({closeDialog, form, flipAnimation, caseRequest, labels}) => <SharingDialog
  closeDialog={closeDialog} withFormWrap
  title={labels.firstStepTitle || (caseRequest ? "Share Expert View report" : "Share medical record")}>
  <FlipDiv
    key="loading"
    css="padding:12rem 0"
    onEnter={loadingEnter}
    onLeave={loadingLeave}
    useAnimation={flipAnimation}
  >
    <Loading autoFocus absolute size={40} borderWidth={6}/>
  </FlipDiv>
</SharingDialog>


export const AuthorizedShareDialog = ({
                                        closeDialog, closeWithNotification, authorization, confirmDialog, shareableLink, shareLink, form, transform, caseRequest, infoCard, flipAnimation, labels,
  defaultFormValues,getLink,
                                        ...rest}) => {
  const {providerInfo, jwt, study, patientName, hasReport} = authorization

  const computedInfoCard = callIfFunction(infoCard,authorization)

  return !confirmDialog.isOpen
    ? <SharingDialog
      className="JS-copy-focus-container"
      withFormWrap
      closeDialog={closeDialog}
      title={callIfFunction(labels.firstStepTitle,shareableLink.isOpen) || (caseRequest ? "Share Expert View report" : "Share medical record")}
      auxiliarActions={
        shareableLink.isOpen
          ? <Button onClick={shareableLink.close} icon="email">Send via email</Button>
          : <Button onClick={shareableLink.open} icon="link">Get shareable link</Button>
      }
      footer={
        shareableLink.isOpen ? <>
          {closeDialog && ClipboardJS.isSupported() && <Button onClick={closeDialog}>Cancel</Button>}
          <CopyButton url={shareLink.isOpen} highlight onCopy={(supported) => closeWithNotification(supported ? 'Shareable link copied to clipboard' : "You've finished the share function")}/>
        </> : <>
          {closeDialog && <Button onClick={closeDialog}>Cancel</Button>}
          <Button highlight type="submit" onClick={form.willSubmitForm}>Send email</Button>
        </>
      }
    >
      <FlipDiv
        key="share"
        onEnter={loadingEnter}
        onLeave={loadingLeave}
        useAnimation={flipAnimation}
      >
        <FirstStep
          closeDialog={closeDialog}
          caseRequest={caseRequest}
          providerInfo={providerInfo}
          study={study}
          infoCard={computedInfoCard}
          hasReport={hasReport}
          jwt={jwt}
          authorization={authorization}
          transform={transform}
          closeWithNotification={closeWithNotification}
          shareableLink={shareableLink}
          shareLink={shareLink}
          form={form}
          getLink={getLink}
          labels={labels}
          defaultFormValues={defaultFormValues}
          action={confirmDialog.openWith}
          {...rest}
        />
      </FlipDiv>
    </SharingDialog>
    : <SecondStepDialog
      form={form}
      labels={labels}
      closeWithNotification={closeWithNotification}
      closeDialog={closeDialog}
      infoCard={computedInfoCard}
      caseRequest={caseRequest}
      confirmDialog={confirmDialog}
    />
}


const FirstStep = ({
                     closeDialog, action, form, jwt, study, providerInfo, authorization, closeWithNotification, infoCard, hasReport, shareableLink, shareLink, caseRequest, transform, suggestedEmails, suggestionLabel, shareDefaults,
  defaultFormValues, getLink, labels
}) => {

  const linkFetch = useMemo(() => (params) => getLink(jwt, params), [jwt])

  const loadingShareableLink = useToggle(true)
  const autocompleteRef = useRef()

  const flipAnimation = useLazyConstant(willUseFlipSwitch)

  useEffect(() => {
    if (!form.getValues('emails')) {
      form.setValues(callIfFunction(defaultFormValues,{providerInfo,hasReport,caseRequest,shareDefaults}),false)
    }
  }, [])

  useEffect(() => updateShareableLink(), [shareableLink.isOpen])

  const updateShareableLink = () => {
    form.clearErrors()
    shareLink.close()
    if (shareableLink.get()) {
      form.setValue('emails', (emails) => emails ? emails.filter(validateEmail) : undefined)
      loadingShareableLink.wait(
        linkFetch(transform(form.getValues()))
          .then(({url}) => shareLink.openWith(url))
          .catch(() => {
            form.setError('shareLink', 'Link failed to load')
            shareLink.close()
          })
      )
    } else {
      loadingShareableLink.open()
    }
  }

  const addEmail = (email) => {
    autocompleteRef.current.addValue(email)
  }

  const filteredSuggestedEmails = suggestedEmails.filter(email => (form.getValue("emails") || []).indexOf(email) < 0)

  return <>
    <p>{labels.firstStepSubTitle}</p>
    <ExamIdentification>
      {infoCard}
      {
        !caseRequest && hasReport && !!providerInfo.sharingOptions.includeStudyReport &&
        <ToggleCheckboxComponent
          onChange={updateShareableLink}
          path="shareStudyReport"
          label={labels.reportToggle || "Include study report"}
        />
      }
    </ExamIdentification>
    {
      providerInfo &&
      <Field label="Security Options">
        <ToggleCheckboxComponent disabled={!providerInfo.phiProtectionImplemented} onChange={updateShareableLink}
                                 path="sendSecurely" label="Send securely"
                                 descText={!providerInfo.phiProtectionImplemented
                                   ? 'Medical records may not be shared securely when either the patient date of birth or patient name is not available.'
                                   : (providerInfo.phiProtectionAttributes.dob && providerInfo.phiProtectionAttributes.lastName)
                                     ? 'Requires the recipient to know the patient’s date of birth and last name.'
                                     : providerInfo.phiProtectionAttributes.dob
                                       ? 'Requires the recipient to know the patient’s date of birth.'
                                       : 'Requires the recipient to know the patient’s last name.'}/>
        {
          !caseRequest &&
          <ToggleCheckboxComponent onChange={updateShareableLink} path="allowImageDownload"
                                   label="Allow recipients to download images"/>
        }
        {/*{
        caseRequestInfo &&
        <ToggleCheckboxComponent onChange={updateShareableLink} path="shareCase"
                                 label="Share case"/>
      }
      {
        caseRequestInfo &&
        <ToggleCheckboxComponent onChange={updateShareableLink} path="allowSubmit"
                                 label="Allow submit"/>
      }*/}
        <ExpirationSelect onChange={updateShareableLink}/>
      </Field>
    }

    <FlipDiv
      key={shareableLink.isOpen}
      useAnimation={flipAnimation}
    >
      {
        shareableLink.isOpen
          ? <CopyLinkComponent label="Copy and send the link below to share your record" url={shareLink.isOpen}
                               key={shareLink.isOpen} loadingShareableLink={loadingShareableLink.isOpen}/>
          : <div>
            <AutoComplete
              ref={autocompleteRef}
              form={form}
              getLabel={_.identity}
              minQueryLength={1}
              confirmIfNewKeys={[' ']}
              defaultValue={[]}
              autoSelect
              validation={emailValidation}
              path="emails"
              label="Enter the recipients email address"
              placeholder="example@email.com"
            />
            {
              filteredSuggestedEmails.length > 0 &&
              <div className="suggested-emails"><span>{suggestionLabel || "Suggestions: "}</span>
                {filteredSuggestedEmails.map((email, i) =>
                  <span key={email}>
                        <a onClick={() => addEmail(email)}>{email}</a>
                    {i + 1 === filteredSuggestedEmails.length ? '' : ', '}
                  </span>
                )}
              </div>
            }
            <FieldDescription>Separate multiple email addresses using comma or space.</FieldDescription>
          </div>
      }
    </FlipDiv>
    <AdditionalText>{labels.additionalInfo}</AdditionalText>
    <Errors/>
  </>
}

export const CopyButton = ({url, highlight, success, icon, onCopy, label = (supported) => supported ? 'Copy link & Close' : 'Done'}) => {
  const ref = useRef()
  useEffect(() => {
    const container = ref.current.closest('.JS-copy-focus-container')
    const btn = new ClipboardJS('.copy-to-clipboard', container ? {container} : {})
    return () => btn.destroy()
  }, [])

  return <Button
    buttonRef={ref}
    className="copy-to-clipboard"
    data-clipboard-text={url}
    highlight={highlight} disabled={!url}
    success={success} icon={icon}
    onClick={() => callIfFunction(onCopy,ClipboardJS.isSupported())}
  >
    {callIfFunction(label,ClipboardJS.isSupported())}
  </Button>
}

const SecondStepDialog = ({closeDialog, closeWithNotification, confirmDialog, jwt, form, infoCard, caseRequest, labels}) => {
  return <SharingDialog
    title={labels.secondStepTitle || (caseRequest ? "Share Expert View report" : "Confirm sharing record(s)")}
    closeDialog={closeDialog}
    form={form}
    footer={<>
      <Button onClick={confirmDialog.close}>Return</Button>
      <Button autoFocus highlight isLoading={form.isLoading} type="submit" onClick={form.willSubmitForm}>Confirm
        send</Button>
    </>}
  >
    <p>{labels.secondStepSubTitle}</p>
    <ExamIdentification>
      {infoCard}
    </ExamIdentification>
    <EmailConfirmationListContainer>
      <p><b>{labels.sendingText || (caseRequest ? "Sending Expert View report to:" : "Sending record(s) to:")} </b></p>
      <ul className="sending-email-list">
        <FormValue path="emails">{emails =>
          emails.map(email => <li key={email}>{email}</li>)
        }</FormValue>
      </ul>
    </EmailConfirmationListContainer>
    <AdditionalText>{labels.additionalInfo}</AdditionalText>
    <SecurityDataWarning>
      <h4>This email may contain confidential and protected health care information.</h4>
      <p>
        Please be sure that the email of the recipient has been entered
        correctly and that you are using appropriately confidential mechanisms
        for this communication.
      </p>
    </SecurityDataWarning>
  </SharingDialog>
}


const FlipDiv = ({useAnimation, onEnter = onEnter, onLeave = onLeave, ...props}) => {
  const ref = useRef()
  useAnimation(ref, onEnter, onLeave)
  return <div css="position:relative;" {...props} ref={ref}/>
}


const copyToClipboard = str => {
  const el = document.createElement('textarea')  // Create a <textarea> element
  el.value = str                                 // Set its value to the string that you want copied
  el.setAttribute('readonly', '')                // Make it readonly to be tamper-proof
  el.style.position = 'absolute'
  el.style.left = '-9999px'                      // Move outside the screen to make it invisible
  document.body.appendChild(el)                  // Append the <textarea> element to the HTML document
  const selected =
    document.getSelection().rangeCount > 0        // Check if there is any content selected previously
      ? document.getSelection().getRangeAt(0)     // Store selection if found
      : false                                    // Mark as false to know no selection existed before
  el.select()                                    // Select the <textarea> content
  document.execCommand('copy')                   // Copy - only works as a result of a user action (e.g. click events)
  document.body.removeChild(el)                  // Remove the <textarea> element
  if (selected) {                                 // If a selection existed before copying
    document.getSelection().removeAllRanges()    // Unselect everything on the HTML document
    document.getSelection().addRange(selected)   // Restore the original selection
  }
}

const ShareableLinkInput = styled.div`
  overflow: hidden;
  position: relative;
  padding: 2px;
  margin: -2px;
  ${Loading} {
    bottom: 11px;
    left: 9px;
    position: absolute;
  }
  ${Icon} {
    font-size: 23px;
    position: absolute;
    bottom: 9px;
    left: 10px;
    color: rgba(0,0,0,0.7);
  }
  input {
    overflow:hidden;
    background: rgba(0,0,0,0.05);
    padding-left: 2.75rem;
    font-size: 12px;
    display: block;
    word-break: normal;
  }
  > div {
    margin-bottom: 0;
  }
`

export const CopyLinkComponent = ({url, label, loadingShareableLink}) => {
  const ref = useRef()
  useEffect(() => {
    if (url) {
      ref.current.select()
    }
  }, [])
  return <ShareableLinkInput>
    {loadingShareableLink ? <Loading size={17} borderWidth={3}/> : <Icon icon="link"/>}
    <TextInput readOnly label={label} raw ref={ref} onClick={(e) => e.target.select()}
               disabled={loadingShareableLink}
               value={loadingShareableLink ? 'Generating shareable link...' : (_.isString(url) ? url : '')}/>
  </ShareableLinkInput>
}

const ExpirationSelectButton = styled.div`
  background-color: rgba(0,0,0,0.06);
  border-radius: 6px;
  padding: 0.5rem 2.75rem 0.5rem 1rem;
  min-height: 36px;
  margin-bottom: 1.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: -0.5rem;
  position: relative;
  :hover {
    background-color: rgba(0,0,0,0.08);
  }
  :active {
    background-color: rgba(0,0,0,0.1);
  }
  .help-text {
    font-weight: 400;
    color: ${getColor('gray120')};
    font-size: 12px;
  }
  ${Icon} {
    font-size: 12px;
    color: rgba(0,0,0,0.5);
    right: 1rem;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
  }
`

const ExpirationSelect = ({onChange}) => {
  const menu = usePopupToggle()
  const [expiration, setExpiration] = useFormValue('expirationTime')
  useEffect(() => {
    onChange()
  }, [expiration])
  return <ExpirationSelectButton onClick={menu.toggle}>
    <div><b>Expiration time:</b> <span className="help-text">{EXPIRATION_LABELS[expiration]}</span></div>
    <Icon icon="arrow-down"/>
    {
      menu.isOpen &&
      <ContextMenu fill>
        {EXPIRATION_TIMES.map(time =>
          <Li icon={time === expiration && 'check'} label={EXPIRATION_LABELS[time]} key={time + ''}
              onClick={() => setExpiration(time)}/>
        )}
      </ContextMenu>
    }
  </ExpirationSelectButton>
}
